import { StandardEvents } from '@shopify/events';
// Saved-artwork safety from the Safety Fix branch, with message fallbacks.
const defaults = {
  hmArtworkPreparing: 'Preparing your saved artwork…',
  hmArtworkRestoring: 'Restoring your saved artwork…',
  hmArtworkRestored: 'Saved artwork restored. A new proof approval is required for this order.',
  hmArtworkChanged: 'Artwork changed. This order requires a new proof approval.',
  hmArtworkRestoreError: 'Saved artwork could not be restored. Please upload your file or contact Hilliard Made.',
  hmArtworkWaiting: 'Saved artwork is taking longer to load. Please upload your file or contact Hilliard Made if it does not appear.'
};
const text = (key) => document.documentElement.dataset[key] || defaults[key] || '';
const ACCELERATED_CHECKOUT_RESET_KEY = 'hm:artwork-reset-after-accelerated-checkout';
const ACCELERATED_CHECKOUT_RESET_TTL = 30 * 60 * 1000;
const SAVED_ARTWORK_REORDER_KEY = 'hm:saved-artwork-reorder';
const SAVED_ARTWORK_REORDER_TTL = 30 * 60 * 1000;
const SAVED_ARTWORK_STATUS_TIMEOUT = 10000;
const POST_ADD_RELOAD_DELAY = 200;
const POST_ADD_SUCCESS_KEY = 'hm:post-add-success';
const POST_ADD_SUCCESS_TTL = 2 * 60 * 1000;
const ARTWORK_LABEL_TEXT = 'upload your artwork';
let savedArtworkUploadField = null;
let pendingSavedArtwork = null;
let savedArtworkRestoreInProgress = false;
let savedArtworkRestored = false;
let savedArtworkForm = null;
let savedArtworkFingerprint = '';
let savedArtworkRestoreVersion = 0;
let reloadScheduled = false;
let labelDedupeScheduled = false;
const observedLabelRoots = new WeakSet();
const artworkLabelObserver = new MutationObserver(() => scheduleArtworkLabelDedupe());
function normalizeText(value) { return String(value || '').replace(/\s+/g, ' ').trim(); }
function normalizeArtworkLabel(value) {
  return normalizeText(value).replace(/(?:\s*(?:\*|:|\(required\)|required))+$/gi, '').trim().toLowerCase();
}
function isSafeArtworkLabelCandidate(element) {
  if (!(element instanceof Element) || normalizeArtworkLabel(element.textContent) !== ARTWORK_LABEL_TEXT) return false;
  const interactiveSelector = 'input, textarea, select, button, a, [role="button"], [contenteditable="true"]';
  return !element.matches(interactiveSelector) && !element.querySelector(interactiveSelector);
}
function collectOpenRoots(root = document, roots = []) {
  roots.push(root);
  if (typeof root.querySelectorAll !== 'function') return roots;
  for (const element of root.querySelectorAll('*')) if (element.shadowRoot) collectOpenRoots(element.shadowRoot, roots);
  return roots;
}
function candidateScore(element) {
  if (element.tagName === 'LABEL') return 50;
  if (element.tagName === 'LEGEND') return 40;
  if (element.hasAttribute('for')) return 30;
  return /^H[1-6]$/.test(element.tagName) ? 5 : 10;
}
function dedupeArtworkLabels() {
  const rawCandidates = [];
  for (const root of collectOpenRoots()) {
    if (typeof root.querySelectorAll !== 'function') continue;
    for (const element of root.querySelectorAll('*')) if (isSafeArtworkLabelCandidate(element)) rawCandidates.push(element);
  }
  const candidates = rawCandidates.filter(candidate => !rawCandidates.some(other => other !== candidate && candidate.contains(other)));
  if (candidates.length < 2) return;
  let keep = candidates[0];
  for (const candidate of candidates.slice(1)) if (candidateScore(candidate) > candidateScore(keep)) keep = candidate;
  for (const candidate of candidates) {
    if (candidate === keep) {
      if (candidate.dataset.hmDuplicateArtworkLabel === 'true') {
        candidate.hidden = false;
        candidate.removeAttribute('aria-hidden');
        delete candidate.dataset.hmDuplicateArtworkLabel;
      }
      continue;
    }
    candidate.hidden = true;
    candidate.setAttribute('aria-hidden', 'true');
    candidate.dataset.hmDuplicateArtworkLabel = 'true';
  }
}
function observeArtworkLabelRoots() {
  for (const root of collectOpenRoots()) {
    const target = root instanceof Document ? root.documentElement : root;
    if (!target || observedLabelRoots.has(target)) continue;
    artworkLabelObserver.observe(target, {childList:true,subtree:true,characterData:true});
    observedLabelRoots.add(target);
  }
}
function scheduleArtworkLabelDedupe() {
  if (labelDedupeScheduled) return;
  labelDedupeScheduled = true;
  window.requestAnimationFrame(() => { labelDedupeScheduled=false; observeArtworkLabelRoots(); dedupeArtworkLabels(); });
}
function clearSavedArtworkReorder() { try { window.sessionStorage.removeItem(SAVED_ARTWORK_REORDER_KEY); } catch {} }
const SAVED_ARTWORK_PROPERTY_NAMES = ['_HM Saved Artwork ID','_HM Artwork Revision','_HM Reorder Source','_HM Artwork Type','_HM Proof Status'];
function artworkInputs(form) {
  return [...(form?.elements || [])].filter(input => !input.disabled && /^properties\[(?:upload(?:_artwork)?)(?:_\d+)?\]$/.test(input.name));
}
function artworkFingerprint(form) {
  return JSON.stringify(artworkInputs(form).filter(input => typeof input.value === 'string' && input.value.trim()).map(input => [input.name,input.value]).sort(([a],[b]) => a.localeCompare(b)));
}
function removeSavedArtworkLineProperties(form) {
  for (const input of [...(form?.elements || [])]) if (SAVED_ARTWORK_PROPERTY_NAMES.some(key => input.name === `properties[${key}]`)) input.remove();
}
function invalidateSavedArtwork() {
  const hadSavedArtwork = pendingSavedArtwork || savedArtworkRestored || savedArtworkRestoreInProgress;
  savedArtworkRestoreVersion += 1;
  clearSavedArtworkReorder();
  removeSavedArtworkLineProperties(savedArtworkForm);
  pendingSavedArtwork=null; savedArtworkRestored=false; savedArtworkRestoreInProgress=false; savedArtworkFingerprint='';
  if (hadSavedArtwork) setSavedArtworkStatus(text('hmArtworkChanged'), 'changed');
}
function isSavedArtworkForm(form) {
  return savedArtworkRestored && form === savedArtworkForm && savedArtworkFingerprint !== '[]' && artworkFingerprint(form) === savedArtworkFingerprint;
}
function validateSavedArtworkSubmission(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart/add"]')) return;
  if (isSavedArtworkForm(form)) return;
  if (form === savedArtworkForm) invalidateSavedArtwork();
  removeSavedArtworkLineProperties(form);
  if (event.formData) for (const key of SAVED_ARTWORK_PROPERTY_NAMES) event.formData.delete(`properties[${key}]`);
}
function handleArtworkRemoved(event) { if (savedArtworkForm?.contains(event.target)) invalidateSavedArtwork(); }
function handleArtworkAdded(event) {
  if (!savedArtworkForm || !savedArtworkForm.contains(event.target)) return;
  if (event.detail?.meta?.variant === 'thumbnail' || savedArtworkRestoreInProgress) return;
  if (savedArtworkRestored && !isSavedArtworkForm(savedArtworkForm)) invalidateSavedArtwork();
}
function handleArtworkSelection(event) {
  if (!savedArtworkForm || !savedArtworkForm.contains(event.target)) return;
  if (event.target instanceof HTMLInputElement && event.target.type === 'file') invalidateSavedArtwork();
}
function markPostAddSuccess() {
  try { window.sessionStorage.setItem(POST_ADD_SUCCESS_KEY, JSON.stringify({productPath:window.location.pathname,createdAt:Date.now()})); } catch {}
}
function readPostAddSuccess() {
  try {
    const raw=window.sessionStorage.getItem(POST_ADD_SUCCESS_KEY);
    window.sessionStorage.removeItem(POST_ADD_SUCCESS_KEY);
    if (!raw) return null;
    const record=JSON.parse(raw);
    if (!record?.productPath || !record?.createdAt || record.productPath !== window.location.pathname || Date.now()-Number(record.createdAt)>POST_ADD_SUCCESS_TTL) return null;
    return record;
  } catch { return null; }
}
function getPrimaryProductForm() {
  return document.querySelector('main product-form-component form[action*="/cart/add"]') || document.querySelector('main form[action*="/cart/add"]');
}
function showPostAddSuccess() {
  if (!readPostAddSuccess()) return;
  const form=getPrimaryProductForm();
  if (!form) return;
  const status=document.createElement('div');
  status.dataset.hmCartAddStatus=''; status.setAttribute('role','status'); status.setAttribute('aria-live','polite');
  status.style.cssText='margin-block-end:1rem;padding:.85rem 1rem;border:1px solid #31483B;border-radius:8px;background:#F4F0E7;color:#171717;';
  const message=document.createElement('strong');
  message.textContent=document.documentElement.dataset.hmAddedMessage || 'Added to cart.';
  const link=document.createElement('a'); link.href=(window.Shopify?.routes?.root || '/')+'cart';
  link.textContent=document.documentElement.dataset.hmViewCart || 'View cart';
  link.style.cssText='color:inherit;text-decoration:underline;text-underline-offset:3px;';
  status.append(message,document.createTextNode(' '),link); form.prepend(status);
  for (const name of ['submit','input','change']) form.addEventListener(name,()=>status.remove(),{once:true});
}
function scheduleCleanProductReload() {
  if (reloadScheduled) return;
  reloadScheduled=true; savedArtworkRestoreVersion+=1;
  removeSavedArtworkLineProperties(savedArtworkForm); savedArtworkFingerprint='';
  clearSavedArtworkReorder(); markPostAddSuccess();
  pendingSavedArtwork=null; savedArtworkUploadField=null; savedArtworkRestoreInProgress=false; savedArtworkRestored=false;
  window.setTimeout(()=>window.location.reload(),POST_ADD_RELOAD_DELAY);
}
function handleCartLinesUpdate(event) {
  if (!event.promise || typeof event.promise.then !== 'function') return;
  event.promise.then(result=>{
    const detail=result?.detail;
    if (detail?.source !== 'product-form-component' || detail?.didError !== false) return;
    scheduleCleanProductReload();
  }).catch(()=>{});
}
function isAcceleratedCheckoutInteraction(event) {
  const path=typeof event.composedPath === 'function' ? event.composedPath() : [];
  return path.some(node=>node instanceof Element && node.classList.contains('accelerated-checkout-block'));
}
function markAcceleratedCheckout() { try { window.sessionStorage.setItem(ACCELERATED_CHECKOUT_RESET_KEY,String(Date.now())); } catch {} }
function resetAfterAcceleratedCheckoutReturn() {
  let timestamp=0;
  try { timestamp=Number(window.sessionStorage.getItem(ACCELERATED_CHECKOUT_RESET_KEY)||0); window.sessionStorage.removeItem(ACCELERATED_CHECKOUT_RESET_KEY); } catch { return; }
  if (!timestamp || Date.now()-timestamp>ACCELERATED_CHECKOUT_RESET_TTL) return;
  window.setTimeout(()=>window.location.reload(),50);
}
function readSavedArtworkReorder() {
  try {
    const raw=window.sessionStorage.getItem(SAVED_ARTWORK_REORDER_KEY);
    if (!raw) return null;
    const record=JSON.parse(raw), age=Date.now()-Number(record?.createdAt);
    if (!record?.fileUrl || !record?.productUrl || !record?.artworkId || record.source !== 'approved-production-artwork' || !Number.isFinite(age) || age<0 || age>SAVED_ARTWORK_REORDER_TTL) { clearSavedArtworkReorder(); return null; }
    const expected=new URL(record.productUrl,window.location.origin), artworkUrl=new URL(record.fileUrl);
    if (expected.origin !== window.location.origin || expected.pathname !== window.location.pathname || artworkUrl.protocol !== 'https:') return null;
    return record;
  } catch { return null; }
}
function setSavedArtworkStatus(message,state='loading') {
  const form=getPrimaryProductForm(); if (!form) return;
  let status=document.querySelector('[data-hm-saved-artwork-status]');
  if (!status) { status=document.createElement('div'); status.dataset.hmSavedArtworkStatus=''; status.setAttribute('role','status'); status.style.cssText='margin-block-end:1rem;padding:.75rem 1rem;border:1px solid currentColor;border-radius:8px;'; form.prepend(status); }
  status.dataset.state=state; status.textContent=message;
}
function addSavedArtworkLineProperties(record,form) {
  const properties={'_HM Saved Artwork ID':record.artworkId,'_HM Artwork Revision':record.revision||1,'_HM Reorder Source':'Saved artwork library reference','_HM Artwork Type':'Saved artwork; new proof required','_HM Proof Status':'Proof approval required for this order'};
  if (!form) return;
  for (const [key,value] of Object.entries(properties)) {
    if (value == null || value === '') continue;
    const name=`properties[${key}]`;
    let input=[...form.querySelectorAll('input[type="hidden"]')].find(node=>node.name===name);
    if (!input) { input=document.createElement('input'); input.type='hidden'; input.name=name; form.appendChild(input); }
    input.value=String(value);
  }
}
async function restoreSavedArtwork() {
  if (!pendingSavedArtwork || !savedArtworkUploadField || savedArtworkRestoreInProgress || savedArtworkRestored) return;
  savedArtworkRestoreInProgress=true;
  const record=pendingSavedArtwork, form=savedArtworkForm, version=++savedArtworkRestoreVersion;
  clearSavedArtworkReorder();
  try {
    setSavedArtworkStatus(text('hmArtworkRestoring'));
    const file=await savedArtworkUploadField.uploadFile(record.fileUrl);
    if (version!==savedArtworkRestoreVersion || record!==pendingSavedArtwork || !form?.isConnected) return;
    const inputs=artworkInputs(form).filter(input=>input.value.trim());
    if (!file?.url || inputs.length!==1 || inputs[0].value!==file.url) { invalidateSavedArtwork(); return; }
    savedArtworkFingerprint=artworkFingerprint(form); savedArtworkRestored=true;
    addSavedArtworkLineProperties(record,form);
    setSavedArtworkStatus(text('hmArtworkRestored'),'success');
  } catch {
    if (version!==savedArtworkRestoreVersion) return;
    invalidateSavedArtwork(); setSavedArtworkStatus(text('hmArtworkRestoreError'),'error');
  } finally { if (version===savedArtworkRestoreVersion) savedArtworkRestoreInProgress=false; }
}
function initializeSavedArtworkRestore() {
  observeArtworkLabelRoots(); scheduleArtworkLabelDedupe(); showPostAddSuccess();
  pendingSavedArtwork=readSavedArtworkReorder(); if (!pendingSavedArtwork) return;
  setSavedArtworkStatus(text('hmArtworkPreparing')); restoreSavedArtwork();
  window.setTimeout(()=>{ if (pendingSavedArtwork && !savedArtworkRestored) setSavedArtworkStatus(text('hmArtworkWaiting'),'loading'); },SAVED_ARTWORK_STATUS_TIMEOUT);
}
document.addEventListener(StandardEvents.cartLinesUpdate,handleCartLinesUpdate);
document.addEventListener('submit',validateSavedArtworkSubmission,true);
document.addEventListener('formdata',validateSavedArtworkSubmission,true);
document.addEventListener('change',handleArtworkSelection,true);
window.addEventListener('upload:added',handleArtworkAdded);
window.addEventListener('upload:removed',handleArtworkRemoved);
for (const name of ['pointerdown','click']) document.addEventListener(name,event=>{if(isAcceleratedCheckoutInteraction(event))markAcceleratedCheckout();},true);
window.addEventListener('pageshow',resetAfterAcceleratedCheckoutReturn);
window.addEventListener('options:rendered',scheduleArtworkLabelDedupe);
window.addEventListener('upload:ready',event=>{
  const field=event.detail;
  const eventForm=event.target instanceof Element ? event.target.closest('form[action*="/cart/add"]') : null;
  const form=eventForm || getPrimaryProductForm();
  if (field && typeof field.uploadFile==='function' && form===getPrimaryProductForm()) {
    if (savedArtworkForm && savedArtworkForm!==form) invalidateSavedArtwork();
    savedArtworkForm=form; savedArtworkUploadField=field; restoreSavedArtwork();
  }
  scheduleArtworkLabelDedupe();
});
if (document.readyState==='loading') document.addEventListener('DOMContentLoaded',initializeSavedArtworkRestore,{once:true});
else initializeSavedArtworkRestore();
